<template>
  <div class="ai-init-setup-success">
    <canvas ref="confettiCanvasRef" class="ai-init-setup-success__confetti"></canvas>
    <div class="ai-init-setup-success__content">
      <div class="ai-init-setup-success__logo-wrap">
        <img alt="" class="ai-init-setup-success__logo" :src="aiLogo" />
      </div>
      <div class="ai-init-setup-success__text">
        <div class="ai-init-setup-success__title">🎉 设置完成！</div>
        <div class="ai-init-setup-success__desc">您已完成所有初始设置，现在可以开始探索了</div>
      </div>
      <button class="ai-init-setup-success__button" type="button" @click="$emit('explore')">
        <i class="ai-init-setup-success__button-icon icon-sparkling-2-fill t-iconfont"></i>
        <span>开始探索</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import aiLogo from '@/assets/ai-logo-empty.png'

defineEmits(['explore'])

const confettiCanvasRef = ref()
let animationFrame = 0
let removeResizeListener = null

const COLORS = [
  '#534AB7',
  '#AFA9EC',
  '#EEEDFE',
  '#1D9E75',
  '#5DCAA5',
  '#D85A30',
  '#F0997B',
  '#378ADD',
  '#85B7EB',
  '#BA7517',
  '#EF9F27'
]

const launchConfetti = () => {
  const canvas = confettiCanvasRef.value
  const ctx = canvas?.getContext('2d')
  const parent = canvas?.parentElement
  if (!canvas || !ctx || !parent) return

  let width = 0
  let height = 0
  let frame = 0

  const resize = () => {
    const dpr = Math.max(1, window.devicePixelRatio || 1)
    width = parent.clientWidth
    height = parent.clientHeight
    canvas.width = Math.floor(width * dpr)
    canvas.height = Math.floor(height * dpr)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  const mkParticle = (opts) =>
    Object.assign(
      {
        x: width / 2,
        y: -10,
        vx: (Math.random() - 0.5) * 7,
        vy: 1.5 + Math.random() * 5,
        size: 5 + Math.random() * 7,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.22,
        shape: Math.random() > 0.45 ? 'rect' : 'circle',
        alpha: 1,
        gravity: 0.11 + Math.random() * 0.08,
        delay: Math.floor(Math.random() * 50)
      },
      opts
    )

  resize()

  const wave1 = Array.from({ length: 160 }, () =>
    mkParticle({
      x: width / 2 + (Math.random() - 0.5) * width * 0.55,
      vx: (Math.random() - 0.5) * 9,
      vy: 1 + Math.random() * 6,
      delay: Math.floor(Math.random() * 40)
    })
  )

  const wave2 = Array.from({ length: 90 }, () => {
    const left = Math.random() < 0.5
    return mkParticle({
      x: left ? -12 : width + 12,
      y: Math.random() * height * 0.5,
      vx: left ? 3 + Math.random() * 4 : -(3 + Math.random() * 4),
      vy: 0.8 + Math.random() * 3.5,
      delay: 40 + Math.floor(Math.random() * 30)
    })
  })

  const particles = [...wave1, ...wave2]

  const draw = () => {
    ctx.clearRect(0, 0, width, height)
    let alive = false

    for (const particle of particles) {
      if (frame < particle.delay) {
        alive = true
        continue
      }

      particle.vy += particle.gravity
      particle.vx *= 0.992
      particle.x += particle.vx
      particle.y += particle.vy
      particle.angle += particle.spin

      if (particle.y > height - 60) particle.alpha -= 0.025
      if (particle.alpha <= 0) continue

      alive = true
      ctx.save()
      ctx.globalAlpha = particle.alpha
      ctx.translate(particle.x, particle.y)
      ctx.rotate(particle.angle)
      ctx.fillStyle = particle.color

      if (particle.shape === 'rect') {
        ctx.fillRect(-particle.size / 2, -particle.size / 3.5, particle.size, particle.size / 3.5)
      } else {
        ctx.beginPath()
        ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2)
        ctx.fill()
      }

      ctx.restore()
    }

    frame += 1
    if (alive) {
      animationFrame = requestAnimationFrame(draw)
    }
  }

  animationFrame = requestAnimationFrame(draw)
  window.addEventListener('resize', resize)
  removeResizeListener = () => window.removeEventListener('resize', resize)
}

onMounted(() => {
  launchConfetti()
})

onBeforeUnmount(() => {
  if (animationFrame) cancelAnimationFrame(animationFrame)
  removeResizeListener?.()
})
</script>

<style lang="scss" scoped>
.ai-init-setup-success {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  overflow: hidden;
}

.ai-init-setup-success__confetti {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.ai-init-setup-success__content {
  position: relative;
  z-index: 2;
  width: 696px;
  height: 616px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ai-init-setup-success__logo-wrap {
  width: 169px;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-init-setup-success__logo {
  height: 112px;
  display: block;
}

.ai-init-setup-success__text {
  width: 480px;
  margin-top: 16px;
  text-align: center;
}

.ai-init-setup-success__title {
  height: 20px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #323233;
}

.ai-init-setup-success__desc {
  height: 20px;
  margin-top: 4px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #969799;
}

.ai-init-setup-success__button {
  width: 154px;
  height: 34px;
  margin-top: 16px;
  padding: 8px 12px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(124, 58, 237, 0.15);
  border-radius: 12px;
  background: linear-gradient(282deg, #eef2ff 1%, #f5f3ff 100%);
  font-family: 'PingFang SC', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #5952ff;
  cursor: pointer;
}

.ai-init-setup-success__button-icon {
  margin-right: 8px;
  font-size: 14px;
  line-height: 14px;
  color: #5952ff;
}
</style>
